/**
 * Filtres analytics partagés (Year / Domain / Application / Environment / User) — look V0.
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  assessmentApi,
  domainsApi,
  applicationEnvironmentsApi,
  type DomainDto,
  type ApplicationDto,
  type ApplicationEnvironmentDto,
} from "../../api/api";

export type AnalyticsFiltersState = {
  year: number;
  domain?: string;
  applicationId?: string;
  environmentId?: string;
  userId?: string;
};

type Props = {
  filters: AnalyticsFiltersState;
  onChange: (f: AnalyticsFiltersState) => void;
  showUser?: boolean;
};

export function AnalyticsFilterBar({ filters, onChange, showUser = false }: Props) {
  const { t } = useTranslation();
  const [domains, setDomains] = useState<DomainDto[]>([]);
  const [erps, setErps] = useState<ApplicationDto[]>([]);
  const [environments, setEnvironments] = useState<ApplicationEnvironmentDto[]>([]);

  useEffect(() => {
    void domainsApi.list().then((r) => setDomains(r.domains.filter((d) => d.active)));
    void assessmentApi.listErp().then((r) => setErps(r.applications.filter((e) => e.active)));
    void applicationEnvironmentsApi.list(undefined, false).then((r) => setEnvironments(r.environments));
  }, []);

  // Filtrer les environnements selon l'application sélectionnée
  const visibleEnvs = filters.applicationId
    ? environments.filter((e) => e.applicationId === filters.applicationId)
    : environments;

  const years = Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - i);

  const set = (key: keyof AnalyticsFiltersState, value: string) => {
    const next: AnalyticsFiltersState = { ...filters };
    if (key === "year") {
      next.year = Number(value) || new Date().getFullYear();
    } else if (key === "domain") {
      next.domain = value || undefined;
    } else if (key === "applicationId") {
      next.applicationId = value || undefined;
      next.environmentId = undefined; // reset env when app changes
    } else if (key === "environmentId") {
      next.environmentId = value || undefined;
    } else if (key === "userId") {
      next.userId = value || undefined;
    }
    onChange(next);
  };

  return (
    <div className="sm-card sm-card-pad sm-analytics-filters">
      <label className="sm-label">
        {t("year")}
        <select className="sm-select" value={filters.year} onChange={(e) => set("year", e.target.value)}>
          {years.map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </select>
      </label>
      <label className="sm-label">
        {t("domain")}
        <select
          className="sm-select"
          value={filters.domain ?? ""}
          onChange={(e) => set("domain", e.target.value)}
        >
          <option value="">{t("all")}</option>
          {domains.map((d) => (
            <option key={d.id} value={d.code}>
              {d.code}
            </option>
          ))}
        </select>
      </label>
      <label className="sm-label">
        {t("application")}
        <select
          className="sm-select"
          value={filters.applicationId ?? ""}
          onChange={(e) => set("applicationId", e.target.value)}
        >
          <option value="">{t("all")}</option>
          {erps.map((e) => (
            <option key={e.id} value={e.id}>
              {e.label}
            </option>
          ))}
        </select>
      </label>
      {visibleEnvs.length > 0 && (
        <label className="sm-label">
          {t("environment")}
          <select
            className="sm-select"
            value={filters.environmentId ?? ""}
            onChange={(e) => set("environmentId", e.target.value)}
          >
            <option value="">{t("all")}</option>
            {visibleEnvs.map((e) => (
              <option key={e.id} value={e.id}>
                {e.label}
              </option>
            ))}
          </select>
        </label>
      )}
      {showUser ? (
        <label className="sm-label">
          {t("user_id")}
          <input
            className="sm-input"
            value={filters.userId ?? ""}
            onChange={(e) => set("userId", e.target.value)}
            placeholder="UUID…"
          />
        </label>
      ) : null}
    </div>
  );
}

/** Même palette que `.sm-score-0` … `.sm-score-4` (saisie auto-évaluation). */
const SCORE_BG = ["#fee2e2", "#ffedd5", "#fef9c3", "#dcfce7", "#bbf7d0"] as const;
const SCORE_FG = ["#b91c1c", "#c2410c", "#a16207", "#15803d", "#166534"] as const;

function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace("#", "");
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
}

function lerpHex(a: string, b: string, t: number): string {
  const [ar, ag, ab] = hexToRgb(a);
  const [br, bg, bb] = hexToRgb(b);
  const r = Math.round(ar + (br - ar) * t);
  const g = Math.round(ag + (bg - ag) * t);
  const bl = Math.round(ab + (bb - ab) * t);
  return `#${[r, g, bl].map((n) => n.toString(16).padStart(2, "0")).join("")}`;
}

export function scoreToColor(score: number | null): string {
  if (score === null) return "#F3F4F6";
  const clamped = Math.max(0, Math.min(4, score));
  const i = Math.floor(clamped);
  const next = Math.min(4, i + 1);
  const t = clamped - i;
  if (t < 0.02) return SCORE_BG[i];
  return lerpHex(SCORE_BG[i], SCORE_BG[next], t);
}

export function scoreToText(score: number | null): string {
  if (score === null) return "#9CA3AF";
  const i = Math.max(0, Math.min(4, Math.round(score)));
  return SCORE_FG[i];
}

export function formatScore(score: number | null): string {
  if (score === null) return "·";
  return Number.isInteger(score) ? String(score) : score.toFixed(1);
}

export function lineTitle(line: {
  subProcess: string | null;
  businessProcess: string;
  macroProcess: string;
  task?: string | null;
}): { title: string; subtitle: string } {
  const task = line.task?.trim() && line.task.trim() !== "." ? line.task.trim() : null;
  const sub =
    line.subProcess?.trim() && line.subProcess.trim() !== "." ? line.subProcess.trim() : null;
  return {
    title: task || sub || line.businessProcess,
    subtitle: [line.macroProcess, line.businessProcess, sub && task ? sub : null]
      .filter(Boolean)
      .join(" · "),
  };
}
